/**
 * Form drafts in IndexedDB (T11-04, design/04 §0 "wizard … บันทึก draft อัตโนมัติ", §15):
 * long forms save what was typed every 5 s and when the tab is hidden/closed; coming back offers
 * "restore draft?". Keyed per staff member + form (`<staffId>:<draftKey>`) so two people on one
 * tablet never see each other's drafts. Falls back to memory when IndexedDB is unavailable.
 *
 * Sensitive fields (ID numbers, bank accounts, passwords) must not be put into drafts — forms with
 * such fields pass `draftExclude` (useShopForm).
 */
import { type DBSchema, type IDBPDatabase, openDB } from "idb";

export interface DraftRecord<T = unknown> {
  key: string;
  value: T;
  /** Epoch ms. */
  savedAt: number;
  /** Record version the draft was started from (for "someone changed it meanwhile"). */
  baseVersion?: number | null;
}

interface DraftDb extends DBSchema {
  drafts: { key: string; value: DraftRecord };
}

const DB_NAME = "tcg-admin-drafts";
/** Drafts older than this are dropped (a week of holidays). */
export const DRAFT_MAX_AGE_MS = 14 * 24 * 3600 * 1000;

let dbPromise: Promise<IDBPDatabase<DraftDb> | null> | null = null;
const memory = new Map<string, DraftRecord>();

function database(): Promise<IDBPDatabase<DraftDb> | null> {
  if (!dbPromise) {
    dbPromise =
      typeof indexedDB === "undefined"
        ? Promise.resolve(null)
        : openDB<DraftDb>(DB_NAME, 1, {
            upgrade(db) {
              db.createObjectStore("drafts", { keyPath: "key" });
            },
          }).catch(() => null);
  }
  return dbPromise;
}

export async function saveDraft<T>(record: DraftRecord<T>): Promise<void> {
  const db = await database();
  if (!db) {
    memory.set(record.key, record);
    return;
  }
  await db.put("drafts", record as DraftRecord);
}

export async function loadDraft<T>(
  key: string,
  now: number = Date.now(),
): Promise<DraftRecord<T> | null> {
  const db = await database();
  const record = db ? await db.get("drafts", key) : memory.get(key);
  if (!record) {
    return null;
  }
  if (now - record.savedAt > DRAFT_MAX_AGE_MS) {
    await deleteDraft(key);
    return null;
  }
  return record as DraftRecord<T>;
}

export async function deleteDraft(key: string): Promise<void> {
  memory.delete(key);
  const db = await database();
  if (db) {
    await db.delete("drafts", key);
  }
}

/** Test helper: forget the cached connection (fake-indexeddb resets between tests). */
export function resetDraftStoreForTests(): void {
  dbPromise = null;
  memory.clear();
}

// ---- pending autosaves are flushed on demand (session expiry, pagehide) ----------------------
const flushers = new Set<() => Promise<void>>();

export function registerDraftFlusher(flush: () => Promise<void>): () => void {
  flushers.add(flush);
  return () => flushers.delete(flush);
}

export async function flushDrafts(): Promise<void> {
  await Promise.all([...flushers].map((flush) => flush().catch(() => undefined)));
}
